'use client';

import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/buttons';
import { ImageComponent } from '@/components/Image';
import { NativeSlider } from '@/components/NativeSlider';
import { SliderArrow } from '@/components/NativeSlider/helpers';
import { SectionTitle } from '@/components/SectionTitle';
import { MediaType } from '@/helpers/enums';
import { useResponsive } from '@/modules/client';
import { useCMSActiveTournaments } from '@/modules/tournaments';
import type { ITournamentCard } from '@/modules/tournaments/components/TournamentCard';
import { useGetCommonStore } from '@/store';
import { TournamentSlide } from './components/TournamentSlise';
import {
  MAX_TOURNAMENT_SLIDES,
  MAX_TOURNAMENT_SLIDES_DESKTOP_L,
  slideGrid,
} from './helpers';

import { ScTournamentsSlider } from './styled';

export type TTournamentSlide = ITournamentCard | { type: 'showAllTournaments' };

const TournamentsSlider = () => {
  const { t } = useTranslation('common');
  const { activeTournaments } = useCMSActiveTournaments();
  const { currentMediaType } = useResponsive();
  const useCommonStore = useGetCommonStore();
  const isMobileDevice = useCommonStore.use.isMobileDevice();

  const isDesktopL = useMemo(
    () => currentMediaType === MediaType.DESKTOP_L,
    [currentMediaType]
  );

  const displayedTournaments = useMemo(() => {
    const length = isDesktopL
      ? MAX_TOURNAMENT_SLIDES_DESKTOP_L
      : MAX_TOURNAMENT_SLIDES;
    return activeTournaments.slice(0, length);
  }, [activeTournaments, isDesktopL]);

  return (
    <ScTournamentsSlider data-testid="tournaments-slider">
      <NativeSlider<TTournamentSlide>
        arrows={{
          type: SliderArrow.OUTSIDE,
          componentLeft: (
            <SectionTitle>
              <span className="title">
                {t('menu.tournaments')}
                <span className="after-title">
                  <ImageComponent
                    alt="icon"
                    height={24}
                    src="/tournamentIcon.webp"
                    width={24}
                  />
                </span>
              </span>
            </SectionTitle>
          ),
          componentRight: (
            <Button
              autoPaddingHorizontal
              className="all-btn"
              to="/tournaments"
              xSmall
            >
              {t('gameCategories.all')}
            </Button>
          ),
        }}
        slide={(slide) => <TournamentSlide slide={slide} />}
        slideGrid={slideGrid}
        slides={[...displayedTournaments, { type: 'showAllTournaments' }]}
        slidesPerView={isMobileDevice ? 1.1 : 1}
      />
    </ScTournamentsSlider>
  );
};

export default TournamentsSlider;
